import axios, { AxiosError } from 'axios';
import { getToken, setToken as storeToken, isLoggedOut } from './tokenStore';
import { Sentry } from '../sentry';

function parseJwt(jwt: string) {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  return JSON.parse(jsonPayload);
}

function getExpiresAt(token: string) {
  const payload = parseJwt(token);
  return payload.exp;
}

export function isTokenExpired(token: string): boolean {
  try {
    const exp = getExpiresAt(token);
    return exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function getAccessToken(): string | null {
  return getToken();
}

export function setTokens(token: string) {
  const exp = getExpiresAt(token);
  storeToken(token, exp);
}

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

export interface TokenRefreshCallbacks {
  onSuccess?: (token: string) => void;
  onError?: (error: any) => void;
}

export async function refreshAccessToken(
  callbacks?: TokenRefreshCallbacks
): Promise<string | null> {
  if (isLoggedOut()) {
    Sentry.addBreadcrumb({
      category: 'auth.refresh',
      level: 'warning',
      message: 'Token refresh skipped — session marked logged out',
      data: { reason: 'logged_out' },
    });
    return null;
  }

  if (isRefreshing) {
    Sentry.addBreadcrumb({
      category: 'auth.refresh',
      level: 'info',
      message: 'Token refresh queued behind an in-flight refresh',
      data: { reason: 'concurrent', queuedCallers: refreshQueue.length + 1 },
    });
    return new Promise((resolve) => {
      refreshQueue.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const baseURL =
      process.env.REACT_APP_API_URL_AXIOS ||
      window.env?.REACT_APP_API_URL_AXIOS;

    const refreshAxios = axios.create({ baseURL, withCredentials: true });
    const response = await refreshAxios.post('/accounts/refresh_token');

    const { token } = response.data;
    setTokens(token);

    if (callbacks?.onSuccess) {
      callbacks.onSuccess(token);
    }

    Sentry.addBreadcrumb({
      category: 'auth.refresh',
      level: 'info',
      message: 'Token refresh succeeded',
      data: { reason: 'success', queuedCallers: refreshQueue.length },
    });

    refreshQueue.forEach((resolve) => resolve(token));
    refreshQueue = [];

    return token;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'auth.refresh',
      level: 'error',
      message: 'Token refresh request failed — resolving callers with null',
      data: {
        reason: 'refresh_request_failed',
        status: (error as AxiosError)?.response?.status ?? null,
        queuedCallers: refreshQueue.length,
      },
    });
    console.error('Refresh Token Error', error);

    if (callbacks?.onError) {
      callbacks.onError(error);
    }

    refreshQueue.forEach((resolve) => resolve(null));
    refreshQueue = [];
    return null;
  } finally {
    isRefreshing = false;
  }
}
